import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  from,
  map,
  mergeMap,
  filter,
  toArray,
  flatMap,
  switchMap,
  tap,
} from 'rxjs';

declare global {
  interface Window {
    Liferay: {
      authToken: string;
      ThemeDisplay: {
        getScopeGroupId(): number;
        getCompanyId(): number;
      };
    };
  }
}

interface ContentField {
  contentFieldValue: {
    data: string | number;
  };
  dataType: 'string' | 'boolean' | 'date' | 'document';
  name: string;
  label: string;
  repeatable: boolean;
}

interface SearchDocument {
  dateCreated: string;
  dateModified: string;
  description: string;
  score: number;
  title: string;
  embedded: {
    id: number;
    contentFields: ContentField[];
    contentStructureId: number;
    keywords: string[];
  };
}

interface SearchFile {
  dateCreated: string;
  dateModified: string;
  description: string;
  score: number;
  title: string;
  embedded: {
    id: number;
    documentFolderId: number;
    documentFolderExternalReferenceCode: string;
    encodingFormat: string;
    fileExtension: string;
    fileName: string;
    sizeInBytes: number;
    keywords: string[];
    contentUrl: string;
  };
}

export interface SearchResult {
  dateCreated: string;
  dateModified: string;
  docDate: string;
  description: string;
  score: number;
  title: string;
  id: number;
  url: string;
  docUrl: string;
  isFile: boolean;
  isDocument: boolean;
  keywords: string[];
  type: string;
  norm: string[];
  publiziert: boolean;
  zusammenfassung: string;
  quelle: string;
  geschaeftszahl: string;
  sizeInBytes: number;
}

interface ElasticSuggest {
  suggest: {
    juspediaSuggest: [
      {
        text: string;
        offset: number;
        length: number;
        options: [
          {
            text: string;
            score: number;
            freq: number;
          },
        ];
      },
    ];
  };
}

@Injectable({
  providedIn: 'root',
})
export class FacettensucheService {
  private queryUrl = '/o/search/v1.0/search';

  constructor(private http: HttpClient) {}

  suggest(query: string): Observable<string> {
    const companyId = window.Liferay.ThemeDisplay.getCompanyId();
    const elasticURL = `/liferay-${companyId}/_search`;
    const data = {
      suggest: {
        juspediaSuggest: {
          text: query,
          term: {
            field: 'content_de_DE',
          },
        },
      },
    };
    return this.http
      .post<ElasticSuggest>(elasticURL, data, {
        headers: {
          Authorization: `Basic ${btoa('elastic:juspedia')}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        mergeMap((r) => from(r.suggest.juspediaSuggest)),
        tap((r) => console.log('after first mergeMap', r)),
        mergeMap((r) => from(r.options)),
        tap((r) => console.log('after second mergeMap', r)),
        map((option) => option.text),
        tap((r) => console.log('after map', r)),
        toArray(),
        tap((r) => console.log('after toArray', r)),
        map((words) => [query, ...words].join(' ')),
      );
  }

  search(query: string): Observable<SearchResult[]> {
    const csrfToken = window.Liferay.authToken;
    const query2 = encodeURIComponent(query);
    const params = 'nestedFields=embedded&pageSize=10000';
    const search = `${this.queryUrl}?${params}&search=${query2}`;
    return this.http
      .post<any>(
        search,
        {},
        {
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        },
      )
      .pipe(
        // die Suchergebnisse raussuchen
        map((response) => response.items),
        // das Array expandieren
        mergeMap((items) => from(items)),
        // nach Dokumenten und Dateien filtern
        filter(
          (i: any) =>
            i.embedded.sizeInBytes || i.embedded.contentFields?.length > 0,
        ),
        // fuer Dateien und Dokumente eine gemeinsame Struktur erstellen
        map((r: SearchFile | SearchDocument) => {
          let result: SearchResult = {
            dateCreated: r.dateCreated.substring(0, 10),
            dateModified: r.dateModified.substring(0, 10),
            docDate: r.dateCreated.substring(0, 10),
            score: r.score,
            description: r.description,
            title: r.title,
            id: r.embedded.id,
            isFile: false,
            isDocument: false,
            zusammenfassung: '',
            publiziert: false,
            type: '',
            norm: [],
            quelle: '',
            geschaeftszahl: '',
            keywords: r.embedded.keywords,
            sizeInBytes: 0,
            url: '',
            docUrl: '',
          };
          if ('contentFields' in r.embedded) {
            // vermutlich ein Dokument
            const doc = r as SearchDocument;
            result.url = 'dokumente?mode=view&doc-id=' + doc.embedded.id;
            result.docUrl = result.url;
            // im Moment noch nicht sicher ein Dokument - wenn wir was finden wird es gesetzt
            result.isDocument = false;
            // die contentFields nach Werten durchsuchen
            for (const f of doc.embedded.contentFields) {
              switch (f.name) {
                case 'btPubliziert':
                  let boolVal = f.contentFieldValue.data as string;
                  result.publiziert = boolVal === 'true';
                  result.isDocument = true;
                  break;
                case 'mlNorm':
                  let strVal = f.contentFieldValue.data as string;
                  result.norm = strVal.split('\n').filter((l) => l !== '');
                  result.isDocument = true;
                  break;
                case 'txTyp':
                  result.type = f.contentFieldValue.data as string;
                  result.isDocument = true;
                  break;
                case 'txReferenz':
                  result.geschaeftszahl = f.contentFieldValue.data as string;
                  result.isDocument = true;
                  break;
                case 'txQuelle':
                  result.quelle = f.contentFieldValue.data as string;
                  result.isDocument = true;
                  break;
                case 'rtZusammenfassung':
                  result.zusammenfassung = f.contentFieldValue.data as string;
                  result.isDocument = true;
                  break;
                case 'dtDatum':
                  result.docDate = (
                    f.contentFieldValue.data as string
                  ).substring(0, 10);
                  result.isDocument = true;
                  break;
              }
            }
          }
          if ('documentFolderId' in r.embedded) {
            // vermutlich eine Datei
            const file = r as SearchFile;
            result.isFile =
              file.embedded.documentFolderExternalReferenceCode !== undefined &&
              file.embedded.documentFolderExternalReferenceCode.length < 20;
            result.publiziert = true;
            result.type = 'angehängte Datei';
            result.sizeInBytes = file.embedded.sizeInBytes;
            result.zusammenfassung = file.description;
            result.url = file.embedded.contentUrl;
            result.docUrl =
              'dokumente?mode=view&doc-id=' +
              file.embedded.documentFolderExternalReferenceCode;
          }
          return result;
        }),
        // filter nach Dokument und File
        filter((v: SearchResult) => v.isFile || v.isDocument),
        // wieder ein Array anlegen
        toArray(),
      );
  }
}

/*
Ergebnis der Suche:

{
  "items": [
    {
      "dateCreated": "2025-03-12T10:44:45Z",
      "dateModified": "2025-03-12T10:44:45Z",
      "description": "Juspedia \n\n- Wissensplattform \n\nWien, 2024 \n\n\n\n \n\n \n\nImpressum \n\nMedieninhaber, Verleger und Herausgeber:  \n\nBundesministerium für Klimaschutz, Umwelt, Energie, Mobilität, \n\nInnovation und...",
      "embedded": {
        "actions": {},
        "adaptedImages": [],
        "contentUrl": "/documents/20117/34071/20250312104444-Anforderungen_Juspedia_20240916.pdf/6e81e3d3-7dc0-af8a-0239-40e3ea851357?version=1.0&t=1741776285202&download=true",
        "creator": {
          "additionalName": "",
          "contentType": "UserAccount",
          "familyName": "Testuser",
          "givenName": "BMK",
          "id": 32147,
          "name": "BMK Testuser"
        },
        "customFields": [],
        "dateCreated": "2025-03-12T10:44:45Z",
        "dateModified": "2025-03-12T10:44:45Z",
        "description": "",
        "documentFolderExternalReferenceCode": "5169c95f-d70c-c4e3-a92e-c419332abf9f",
        "documentFolderId": 34071,
        "documentType": {
          "availableLanguages": [],
          "contentFields": [],
          "description": "",
          "name": "Einfaches Dokument"
        },
        "encodingFormat": "application/pdf",
        "externalReferenceCode": "6e81e3d3-7dc0-af8a-0239-40e3ea851357",
        "fileExtension": "pdf",
        "fileName": "20250312104444-Anforderungen_Juspedia_20240916.pdf",
        "friendlyUrlPath": "20250312104444-anforderungen_juspedia_20240916-pdf",
        "id": 35562,
        "keywords": [],
        "numberOfComments": 0,
        "relatedContents": [],
        "renderedContents": [],
        "siteId": 20117,
        "sizeInBytes": 856786,
        "taxonomyCategoryBriefs": [],
        "title": "20250312104444-Anforderungen_Juspedia_20240916.pdf"
      },
      "itemURL": "https://juspedia-dev.structview.net/o/headless-delivery/v1.0/documents/35562",
      "score": 9.362088,
      "title": "20250312104444-Anforderungen_Juspedia_20240916.pdf"
    },
    {
      "dateCreated": "2025-03-12T10:48:06Z",
      "dateModified": "2025-03-12T10:48:06Z",
      "description": "Juspedia \n\n- Wissensplattform \n\nWien, 2024 \n\n\n\n \n\n \n\nImpressum \n\nMedieninhaber, Verleger und Herausgeber:  \n\nBundesministerium für Klimaschutz, Umwelt, Energie, Mobilität, \n\nInnovation und...",
      "embedded": {
        "actions": {},
        "adaptedImages": [],
        "contentUrl": "/documents/20117/34071/20250312104805-Anforderungen_Juspedia_20240916.pdf/8f243880-db61-97f1-f78d-a30f7329552f?version=1.0&t=1741776486369&download=true",
        "creator": {
          "additionalName": "",
          "contentType": "UserAccount",
          "familyName": "Testuser",
          "givenName": "BMK",
          "id": 32147,
          "name": "BMK Testuser"
        },
        "customFields": [],
        "dateCreated": "2025-03-12T10:48:06Z",
        "dateModified": "2025-03-12T10:48:06Z",
        "description": "",
        "documentFolderExternalReferenceCode": "5169c95f-d70c-c4e3-a92e-c419332abf9f",
        "documentFolderId": 34071,
        "documentType": {
          "availableLanguages": [],
          "contentFields": [],
          "description": "",
          "name": "Einfaches Dokument"
        },
        "encodingFormat": "application/pdf",
        "externalReferenceCode": "8f243880-db61-97f1-f78d-a30f7329552f",
        "fileExtension": "pdf",
        "fileName": "20250312104805-Anforderungen_Juspedia_20240916.pdf",
        "friendlyUrlPath": "20250312104805-anforderungen_juspedia_20240916-pdf",
        "id": 35597,
        "keywords": [],
        "numberOfComments": 0,
        "relatedContents": [],
        "renderedContents": [],
        "siteId": 20117,
        "sizeInBytes": 856786,
        "taxonomyCategoryBriefs": [],
        "title": "20250312104805-Anforderungen_Juspedia_20240916.pdf"
      },
      "itemURL": "https://juspedia-dev.structview.net/o/headless-delivery/v1.0/documents/35597",
      "score": 9.362088,
      "title": "20250312104805-Anforderungen_Juspedia_20240916.pdf"
    },
    {
      "dateCreated": "2025-03-12T10:32:20Z",
      "dateModified": "2025-03-12T10:32:20Z",
      "description": "Juspedia \n\n- Wissensplattform \n\nWien, 2024 \n\n\n\n \n\n \n\nImpressum \n\nMedieninhaber, Verleger und Herausgeber:  \n\nBundesministerium für Klimaschutz, Umwelt, Energie, Mobilität, \n\nInnovation und...",
      "embedded": {
        "actions": {},
        "adaptedImages": [],
        "contentUrl": "/documents/20117/34071/20250312103219-Anforderungen_Juspedia_20240916.pdf/1cc0c87d-9396-0a74-d489-1fa9d37cffbe?version=1.0&t=1741775540489&download=true",
        "creator": {
          "additionalName": "",
          "contentType": "UserAccount",
          "familyName": "Testuser",
          "givenName": "BMK",
          "id": 32147,
          "name": "BMK Testuser"
        },
        "customFields": [],
        "dateCreated": "2025-03-12T10:32:20Z",
        "dateModified": "2025-03-12T10:32:20Z",
        "description": "",
        "documentFolderExternalReferenceCode": "5169c95f-d70c-c4e3-a92e-c419332abf9f",
        "documentFolderId": 34071,
        "documentType": {
          "availableLanguages": [],
          "contentFields": [],
          "description": "",
          "name": "Einfaches Dokument"
        },
        "encodingFormat": "application/pdf",
        "externalReferenceCode": "1cc0c87d-9396-0a74-d489-1fa9d37cffbe",
        "fileExtension": "pdf",
        "fileName": "20250312103219-Anforderungen_Juspedia_20240916.pdf",
        "friendlyUrlPath": "20250312103219-anforderungen_juspedia_20240916-pdf",
        "id": 35522,
        "keywords": [],
        "numberOfComments": 0,
        "relatedContents": [],
        "renderedContents": [],
        "siteId": 20117,
        "sizeInBytes": 856786,
        "taxonomyCategoryBriefs": [],
        "title": "20250312103219-Anforderungen_Juspedia_20240916.pdf"
      },
      "itemURL": "https://juspedia-dev.structview.net/o/headless-delivery/v1.0/documents/35522",
      "score": 9.362088,
      "title": "20250312103219-Anforderungen_Juspedia_20240916.pdf"
    }
  ],
  "lastPage": 1,
  "page": 1,
  "pageSize": 20,
  "totalCount": 3
}
*/
